import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import { Input, InputGroup, InputRightElement } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Icon,
  Text,
  useDisclosure,
  VStack,
  List,
  ListItem,
} from '@chakra-ui/react'
import type { FieldInputProps, FieldProps } from 'formik'
import { Field, Formik, useFormikContext } from 'formik'
import React, { useContext, useState, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5'
import { RiLockPasswordLine } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type {
  ChangePasswordErrorResponse,
  ChangePasswordSuccessResponse,
  ChangePasswordValidationErrorResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'
import { CheckCircleIcon, InfoOutlineIcon } from '@chakra-ui/icons'

interface Props {
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  old_pass?: string
  new_pass: string
  confirm_pass: string
}

interface PasswordInputProps {
  isInputVisible: boolean
  showToggle: boolean
  field: FieldInputProps<string>
  onToggle(): void
}

const PASSWORD_MIN_LENGTH = 8
const PASSWORD_MAX_LENGTH = 100
const LOWERCASE_REGEX = /[a-z]/
const UPPERCASE_REGEX = /[A-Z]/
const DIGIT_REGEX = /\d/

const VALIDATION_MESSAGES = {
  validLength: 'password_validLength',
  hasLowercase: 'password_hasLowercase',
  hasUppercase: 'password_hasUppercase',
  hasDigit: 'password_hasDigit',
}

type PasswordValidationState = {
  validLength: boolean,
  hasLowercase: boolean,
  hasUppercase: boolean,
  hasDigit: boolean,
}

const validatePassword = (password: string): PasswordValidationState => ({
  validLength:
    password?.length >= PASSWORD_MIN_LENGTH &&
    password?.length <= PASSWORD_MAX_LENGTH,
  hasLowercase: LOWERCASE_REGEX.test(password),
  hasUppercase: UPPERCASE_REGEX.test(password),
  hasDigit: DIGIT_REGEX.test(password),
})

const PasswordValidation = ({ validations }) => {
  const { t } = useTranslation()

  return (
    <Box
      borderWidth="1px"
      borderRadius="16px"
      borderColor="imagekitGray.200"
      padding="12px 16px"
      mt={3}
      backgroundColor="imagekitGray.50"
    >
      <Text fontWeight="medium" fontSize="xs" mb={1} color="imagekitGray.600">
        {t('password_must_contain', 'Password must contain:')}
      </Text>
      <List spacing={1}>
        {Object.entries(VALIDATION_MESSAGES).map(([key, messageKey]) => (
          <ListItem
            key={key}
            display="flex"
            alignItems="center"
            fontSize="xs"
            color="imagekitGray.600"
            whiteSpace="nowrap"
          >
            <Icon
              as={validations[key] ? CheckCircleIcon : InfoOutlineIcon}
              color={validations[key] ? 'green.500' : 'imagekitGray.400'}
              mr={2}
            />
            {t(messageKey)}
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

const PasswordInput = ({
  isInputVisible,
  showToggle,
  field,
  onToggle,
}: PasswordInputProps): React.ReactElement => (
  <InputGroup size="md">
    <Input
      {...field}
      type={isInputVisible ? 'text' : 'password'}
      placeholder="Enter password"
    />
    {showToggle && (
      <InputRightElement width="4.5rem">
        {isInputVisible ? (
          <IoEyeOutline onClick={onToggle} />
        ) : (
          <IoEyeOffOutline onClick={onToggle} />
        )}
      </InputRightElement>
    )}
  </InputGroup>
)

const ChangePasswordModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user, refetch: refetchUser } = useContext(UserContext)
  const {
    isOpen: isOldPasswordVisible,
    onToggle: onToggleOldPassword,
    onClose: onCloseOldPassword,
  } = useDisclosure()
  const {
    isOpen: isNewPasswordVisible,
    onToggle: onToggleNewPassword,
    onClose: onCloseNewPassword,
  } = useDisclosure()
  const { request, response } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [formError, setFormError] = useState<string>('')
  const [passwordValidations, setPasswordValidations] = useState<PasswordValidationState>(
    validatePassword(''),
  )

  const initialValues = user?.isPasswordSetByUser
    ? { old_pass: '', new_pass: '', confirm_pass: '' }
    : { new_pass: '', confirm_pass: '' }

  const validationSchema = Yup.object().shape({
    old_pass: Yup.lazy(() => {
      if (user?.isPasswordSetByUser) {
        return Yup.string()
          .min(1, 'Should be of minimum length 1')
          .required('Required')
      }
      return Yup.string().min(1, 'Should be of minimum length 1')
    }),
    new_pass: Yup.string()
      .min(PASSWORD_MIN_LENGTH, VALIDATION_MESSAGES.validLength)
      .max(PASSWORD_MAX_LENGTH, VALIDATION_MESSAGES.validLength)
      .matches(LOWERCASE_REGEX, VALIDATION_MESSAGES.hasLowercase)
      .matches(UPPERCASE_REGEX, VALIDATION_MESSAGES.hasUppercase)
      .matches(DIGIT_REGEX, VALIDATION_MESSAGES.hasDigit)
      .required('Required'),
    confirm_pass: Yup.string()
      .required('Required')
      .oneOf([Yup.ref('new_pass'), null], 'Passwords do not match'),
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setFormError('')
        onClose()
        onCloseOldPassword()
        onCloseNewPassword()
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={initialValues}
          validateOnChange={true}
          validate={(values) => {
            setPasswordValidations(validatePassword(values.new_pass));
            return {}
          }}
          onSubmit={async (values, { setFieldError }): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              const apiResponse = (await request.post(
                `/api/user/${user.id}/change-password`,
                values,
              )) as
                | ChangePasswordSuccessResponse
                | ChangePasswordErrorResponse
                | ChangePasswordValidationErrorResponse

              if (request.error) {
                if (response.status === 403) {
                  toast({
                    description: t('error_submitting_data', 'Error in submitting data. Try refreshing the page.'),
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                  return;
                } else if ((apiResponse as ChangePasswordErrorResponse).reason) {
                  setFormError((apiResponse as ChangePasswordErrorResponse).reason)
                } else if (
                  (apiResponse as ChangePasswordValidationErrorResponse).errors
                    .length > 0
                ) {
                  let newPasswordMessage = ''

                  for (const err of (
                    apiResponse as ChangePasswordValidationErrorResponse
                  ).errors) {
                    if (err.param === 'new_pass') {
                      newPasswordMessage = err.msg
                    }
                    if (
                      !(
                        err.param === 'confirm_pass' &&
                        err.msg === newPasswordMessage
                      )
                    ) {
                      setFieldError(err.param, err.msg)
                    }
                  }
                } else {
                  setFormError(
                    t('something_went_wrong', 'Something went wrong'),
                  )
                }
              } else {
                toast({
                  description: t(
                    'password_has_been_updated',
                    'Password has been updated',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                void refetchUser()
                setFormError('')
                onCloseOldPassword()
                onCloseNewPassword()
                onClose()
              }
            }
          }}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, isValid, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={RiLockPasswordLine}
                  color={colors.appBattleshipGrey}
                  backgroundColor={colors.appPaleGrey}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {user?.isPasswordSetByUser
                  ? t('change_password', 'Change Password')
                  : t('set_password', 'Set Password')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack width="full" spacing="1rem">
                  {formError ? (
                    <Alert status="error" mt="1em">
                      <AlertIcon />
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  ) : null}
                  {user?.isPasswordSetByUser && (
                    <Field name="old_pass">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          marginTop="1em"
                          isInvalid={Boolean(
                            form.errors.old_pass && form.touched.old_pass,
                          )}
                          isRequired
                        >
                          <FormLabel>
                            {t('current_password', 'Current Password')}
                          </FormLabel>
                          <PasswordInput
                            field={field}
                            isInputVisible={isOldPasswordVisible}
                            onToggle={onToggleOldPassword}
                            showToggle={true}
                          />
                          <FormErrorMessage mt="0.25em">
                            {form.errors.old_pass}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}
                  <Field name="new_pass">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        marginTop="0.3em"
                        isInvalid={Boolean(
                          form.errors.new_pass && form.touched.new_pass,
                        )}
                        isRequired
                      >
                        <FormLabel>
                          {t('new_password', 'New Password')}
                        </FormLabel>
                        <PasswordInput
                          field={field}
                          isInputVisible={isNewPasswordVisible}
                          onToggle={onToggleNewPassword}
                          showToggle={true}
                        />
                        <FormErrorMessage mt="0.25em">
                          {![...Object.values(VALIDATION_MESSAGES)].includes(
                            form.errors.new_pass as string,
                          ) && form.errors.new_pass}
                        </FormErrorMessage>
                        <PasswordValidation validations={passwordValidations} />
                      </FormControl>
                    )}
                  </Field>
                  <Field name="confirm_pass">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        marginTop="0.3em"
                        isInvalid={Boolean(
                          form.errors.confirm_pass && form.touched.confirm_pass,
                        )}
                        isRequired
                      >
                        <FormLabel>
                          {t('confirm_password', 'Confirm Password')}
                        </FormLabel>
                        <PasswordInput
                          field={field}
                          isInputVisible={isNewPasswordVisible}
                          onToggle={onToggleNewPassword}
                          showToggle={false}
                        />
                        <FormErrorMessage mt="0.25em">
                          {form.errors.confirm_pass}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default ChangePasswordModal
